import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateEmployee } from 'modules/employees/hooks/useUpdateEmployee'
import { getDirtyValues } from 'modules/employees/lib/getDirtyValues'
import type Employee from 'modules/employees/store/models/Employee'

interface PayrollInformationValues {
  irpfInCents: number | undefined
}

interface Props {
  employee: Employee
  readonly: boolean
  setSubmitEventHandler: (eventHandler: () => void) => void
  setTaxShowSubmit: (dirty: boolean) => void
}

const TaxesSection: React.FC<Props> = ({
  employee,
  readonly,
  setSubmitEventHandler,
  setTaxShowSubmit,
}) => {
  const { update, loading: submitting } = useUpdateEmployee()

  const form = useForm<PayrollInformationValues>({
    defaultValues: {
      irpfInCents: employee.attributes.irpf ?? undefined,
    },
    onSubmit: async (values, { setErrors }) => {
      const result = await update({
        ...values,
        id: employee.id,
        accessId: employee.attributes.access_id,
      })

      const handled = handleResult(result, (errors) => {
        setErrors({ ...errors })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        form.resetForm({ values })
        setTaxShowSubmit(false)
      }

      return handled
    },
  })

  useEffect(() => {
    setSubmitEventHandler(() => {
      track(trackingEvents.employee.editEmployeeInformationSubmit, {
        fields_edited: [
          ...getDirtyValues(
            Object.entries(form.values),
            Object.fromEntries(Object.entries(form.initialValues))
          ),
        ],
        section_edited: 'payroll_information',
        legal_entity_country: employeeLegalEntity(employee).country,
        layout_version: 'v2',
      })

      form.handleSubmit()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.irpfInCents])

  const showSubmit = !readonly && (form.dirty || !form.isValid || submitting)

  useEffect(() => {
    setTaxShowSubmit(showSubmit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubmit])

  const formatIrpf = (value: number | undefined) => {
    if (!value) return undefined

    return (value / 100).toString()
  }

  const onChangeIrpf = (value: string) => {
    const realValue = !value ? undefined : +value * 100
    form.setValues({ ...form.values, irpfInCents: realValue })
  }

  return (
    <Box gap="s24" paddingY="s24">
      <Box
        flexDirection="row"
        paddingBottom="s8"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
      >
        <Text.Header2 color="grey1000" weight="medium">
          {i18n.t('employee_form.payroll_section.taxes_preferences')}
        </Text.Header2>
      </Box>
      <Box flexDirection="row" gap="s24" alignItems="center">
        <Box>
          <TextField
            mandatory
            label={i18n.t('employee_form.irpf.label')}
            type="number"
            value={formatIrpf(form.values.irpfInCents)}
            readonly={readonly}
            onChange={(value) => onChangeIrpf(value)}
            error={form.errors.irpfInCents}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(TaxesSection)
